import { Inject, Injectable, InjectionToken } from '@angular/core';
import { NewRelicEventParams } from '@app/interfaces/new-relic-event.interface';
import { CussService } from '@services/cuss.service';
import { GlobalsService } from './globals.service';
import { Passenger, Reservation } from '@models/micro';
import { METRIC_NAME, NEW_RELIC_EVENT, PAX_TYPE, PNR_ENTRANCE_ORIGIN } from '@app/enums';
import { OfflineEventQueueService } from '@app/services/offline-event-queue.service';
import { environment } from '@env/environment';
import { AvailableLuggageMetrics } from '@app/models/micro/available-luggage-metrics.model';

export const WINDOW = new InjectionToken<Window>('Global window object', {
  factory: () => window,
});

@Injectable({
  providedIn: 'root',
})
export class NewRelicEventsService {
  private NAME_METRIC = METRIC_NAME.KIOSK_AM;
  private readonly sessionFlowStartKey = 'nr_session_flow_start_ms';
  private readonly sessionFlowCompletedKey = 'nr_session_flow_completed';

  /** Eventos que se encolan en localStorage cuando no hay red */
  private readonly OFFLINE_ELIGIBLE_EVENTS: Set<string> = new Set([
    NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_SEARCH,
    NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_INIT,
    NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_RESEARCH_INIT,
    NEW_RELIC_EVENT.KIOSK_ADDRESS_AUTOCOMPLETE_SELECTION,
    NEW_RELIC_EVENT.KIOSK_ADDRESS_SCREEN_TIME,
  ]);

  constructor(
    @Inject(WINDOW) private windowRef: Window & { newrelic?: NewRelic },
    private _cussService: CussService,
    private _globalService: GlobalsService,
    private offlineQueue: OfflineEventQueueService
  ) {}

  /**
   * Sends an event to New Relic with the provided event name and data.
   *
   * @param eventName
   * @param data
   */
  public trackEvent<T extends Record<string, unknown> = Record<string, never>>(
    eventName: string,
    data?: T
  ): void {
    setTimeout(() => {
      try {
        const nr = this.getNewRelicAgent();
        if (!nr?.addPageAction) {
          return;
        }
        const finalMetricData = {
          kiosksID: this._cussService?.KioskID,
          reservationCode: this._globalService?.getPNRLookupAWSInt()?.reservationCode,
          branded_fare: this.getReservation()?.flightSummary?.fareFamily,
          name: this.NAME_METRIC,
          subName: eventName,
          actionSubname: eventName,
          stationId: this.getReservation()?.flightSummary?.departureAirport || '',
          transactionId: this.getReservation()?.flightSummary?.cartId,
          isService: false,
          isError: false,
          isSSBD: true,
          environment: environment.production ? 'production' : 'cert',
          error_message: '',
          ...data,
        } as NewRelicEventParams & T;

        const attrs = this.sanitizePageActionAttributes(finalMetricData as Record<string, unknown>);
        if (eventName === NEW_RELIC_EVENT.SESSION_FLOW_DURATION) {
          console.warn('[NR][trackEvent] session_flow_duration', {
            // actionName: eventName,

            attrs,
          });
        }

        if (!this.offlineQueue.isOnline && this.OFFLINE_ELIGIBLE_EVENTS.has(eventName)) {
          this.offlineQueue.enqueue(eventName, attrs as Record<string, unknown>);
        } else {
          nr.addPageAction(this.NAME_METRIC, attrs);
        }
      } catch (error) {
        console.warn('Error sending event to New Relic', error);
      }
    }, 0);
  }

  public trackPnrEntranceType(origin: PNR_ENTRANCE_ORIGIN): void {
    this.trackEvent(NEW_RELIC_EVENT.PNR_ENTRANCE_TYPE, { origin });
  }

  /**
   * Maletas de la reserva (incluidas / pagadas / redimidas aplicadas) + pasajeros seleccionados.
   */
  public trackAvailableLuggageReservation(
    passengers: Passenger[] | null | undefined,
    selectedPassengers: Passenger[] | null | undefined
  ): void {
    const paxList = (passengers ?? []).filter((p) => p.paxType !== PAX_TYPE.INFANT);
    const selected = (selectedPassengers ?? []).filter((p) => p.paxType !== PAX_TYPE.INFANT);
    const payload = AvailableLuggageMetrics.buildReservationPayload(paxList, selected.length);
    this.trackEvent(NEW_RELIC_EVENT.AVAILABLE_LUGGAGE_RESERVATION, { ...payload });
  }

  public startSessionFlowTimer(): void {
    try {
      const alreadyStarted = Number(sessionStorage.getItem(this.sessionFlowStartKey));
      const alreadyCompleted = sessionStorage.getItem(this.sessionFlowCompletedKey) === 'true';
      if (alreadyStarted && !alreadyCompleted) {
        console.warn('[NR][SessionFlow] Timer ya iniciado', {
          startedAt: alreadyStarted,
          completed: alreadyCompleted,
        });
        return;
      }

      const startedAt = Date.now();
      sessionStorage.setItem(this.sessionFlowStartKey, startedAt.toString());
      sessionStorage.setItem(this.sessionFlowCompletedKey, 'false');
      console.warn('[NR][SessionFlow] Timer iniciado', {
        startedAt,
      });
    } catch (error) {
      console.warn('[NR][SessionFlow] error al iniciar timer', { error });
    }
  }

  public completeSessionFlowTimer<T extends Record<string, unknown> = Record<string, never>>(
    completionStep: string,
    data?: T
  ): void {
    try {
      const alreadyCompleted = sessionStorage.getItem(this.sessionFlowCompletedKey) === 'true';
      if (alreadyCompleted) {
        console.warn('[NR][SessionFlow] Timer ya completado, no se reenvia evento', {
          completionStep,
        });
        return;
      }

      const startedAt = Number(sessionStorage.getItem(this.sessionFlowStartKey));
      if (!startedAt) {
        console.warn('[NR][SessionFlow] No existe inicio de timer, no se puede completar', {
          completionStep,
        });
        return;
      }

      const durationSeconds = Number(((Date.now() - startedAt) / 1000).toFixed(2));
      console.warn('[NR][SessionFlow] Timer completado, enviando metrica', {
        completionStep,
        duration_seconds: durationSeconds,
        startedAt,
      });
      this.trackEvent(NEW_RELIC_EVENT.SESSION_FLOW_DURATION, {
        duration_seconds: durationSeconds,
        completion_step: completionStep,
        ...data,
      });

      sessionStorage.setItem(this.sessionFlowCompletedKey, 'true');
    } catch (error) {
      console.warn('[NR][SessionFlow] error al completar timer', {
        completionStep,
        error,
      });
    }
  }

  private getNewRelicAgent(): NewRelic | undefined {
    try {
      return this.windowRef?.newrelic as NewRelic | undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * addPageAction solo acepta valores primitivos; si mandamos objetos/arrays el SDK puede descartar el evento.
   */
  private sanitizePageActionAttributes(
    raw: Record<string, unknown>
  ): Record<string, string | number | boolean | null> {
    const out: Record<string, string | number | boolean | null> = {};
    for (const [key, value] of Object.entries(raw)) {
      if (value === null || value === undefined) {
        out[key] = null;
        continue;
      }
      const t = typeof value;
      if (t === 'string' || t === 'number' || t === 'boolean') {
        out[key] = value as string | number | boolean;
        continue;
      }
      try {
        out[key] = JSON.stringify(value);
      } catch {
        out[key] = String(value);
      }
    }
    return out;
  }

  /**
   * Gets the reservation stored in the current session.
   * @returns {Reservation | undefined} - Reservation object or `undefined` if no data is found.
   */
  private getReservation(): Reservation | undefined {
    try {
      const raw = sessionStorage.getItem('reservation');
      if (!raw) {
        return undefined;
      }
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const reservation = new Reservation();
      Object.assign(reservation, parsed);
      return reservation;
    } catch {
      return undefined;
    }
  }
}
